import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { VideoJsPlayer } from 'video.js';
import {
  ProgressTrack,
  ProgressInputRange,
  ProgressBufferRange,
} from '../styles';
import { VideoFileData } from '../PlayerModels';
import { isAtTrackEnd } from '../PlayerServices';
import UsePlayerProps from '../../../Store/usePlayerProps';

const VastControlBarProgress = ({ player }: { player: VideoJsPlayer }) => {
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { playback_type } = playerGlobalState.contentConfigs as VideoFileData;
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferPercent, setBufferPercent] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    player.on('timeupdate', handleTimeUpdate);
    player.on('progress', updateBufferPercent);
    player.on('seeking', handleSeeking);
    player.on('seeked', handleSeekEnd);

    return () => {
      player.off('timeupdate', handleTimeUpdate);
      player.off('progress', updateBufferPercent);
      player.off('seeking', handleSeeking);
      player.off('seeked', handleSeekEnd);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  const handleTimeUpdate = () => {
    if (isAtTrackEnd(player)) {
      player.trigger('end');
    }
    if (!isSeeking) {
      setCurrentTime(player.currentTime());
      setDuration(player.duration());
    }
  };

  const updateBufferPercent = () => {
    handleSeekEnd();
    const buffered = player.buffered();
    const duration = player.duration();

    if (buffered.length > 0) {
      const bufferedEnd = buffered.end(buffered.length - 1);
      setBufferPercent((bufferedEnd / duration) * 100);
    }
  };

  const handleSeeking = () => {
    setIsSeeking(true);
  };

  const handleSeekEnd = () => {
    setIsSeeking(false);
  };

  return (
    <>
      <ProgressTrack id="vast-progress-track" ref={trackRef} playingAd={true}>
        <ProgressInputRange
          id="vast-seekBar"
          disabled
          playingAd={true}
          type="range"
          step="any"
          max={duration}
          value={currentTime}
        />
        <Progress
          currentTime={currentTime}
          duration={duration}
          playback_type={playback_type}
          isBehind={player?.liveTracker?.behindLiveEdge()}
        />
        <ProgressBufferRange bufferPercent={bufferPercent} />
      </ProgressTrack>
    </>
  );
};

const Progress = styled.div<{ currentTime: number; duration: number; playback_type?: string, isBehind: boolean }>(
  ({ theme, currentTime, duration, playback_type, isBehind }) => {
    const progressWidth = playback_type === 'live_stream' && !isBehind ? '100%' : `calc(${(currentTime / duration) * 100}% + 0px)`;
    return {
      borderRadius: '0.5rem',
      zIndex: 20,
      position: 'absolute',
      width: progressWidth,
      maxWidth: '100%',
      height: '100%',
      top: 0,
      left: 0,
      backgroundColor: theme.palette.success[900][0],
      pointerEvents: 'none',
    };
  },
);

export default VastControlBarProgress;
